import {
  Appointment,
  Fitter,
  LocationDataGeocoded,
} from '@sc/data-access-models';

import { forEach, groupBy } from 'lodash-es';

export class AppointmentUtils {
  static getAppointmentsGroupedByDatetime(
    appointments: Appointment[]
  ): Map<string, Appointment[]> {
    const appointmentsResult: Map<string, Appointment[]> = new Map();

    for (const appointment of appointments) {
      const date: Date = new Date(appointment.time);
      AppointmentUtils.addAppointmentToMap(
        date.toISOString(),
        appointmentsResult,
        appointment
      );
    }
    return appointmentsResult;
  }

  static getAppointmentsGroupedByFitter(
    appointments: Appointment[],
    fitters: Fitter[],
    sortCondition?: string
  ): Map<string, Appointment[]> {
    let appointmentsResult: Map<string, Appointment[]> = new Map();
    const fitterIds = fitters?.map((fitter) => fitter.id) || [];

    for (const appointment of appointments) {
      const fitterId: string = appointment.fitterId;

      if (!fitters?.length || fitterIds.includes(fitterId)) {
        AppointmentUtils.addAppointmentToMap(
          fitterId,
          appointmentsResult,
          appointment
        );
      }
    }

    if (sortCondition === 'rating') {
      let sortedFitterIds = null;

      sortedFitterIds = fitters
        ?.slice()
        .sort((fitterA: Fitter, fitterB: Fitter) => {
          return (
            (fitterB.globalScore ? fitterB.globalScore : -1) -
            (fitterA.globalScore ? fitterA.globalScore : -1)
          );
        })
        .map((fitter) => fitter.id);

      appointmentsResult = new Map(
        [...appointmentsResult].sort((appointmentA, appointmentB) => {
          return sortedFitterIds?.indexOf(appointmentA[0]) >
            sortedFitterIds?.indexOf(appointmentB[0])
            ? 1
            : -1;
        })
      );
    }

    return appointmentsResult;
  }

  private static addAppointmentToMap(
    key: string,
    appointmentsMap: Map<string, Appointment[]>,
    appointment: Appointment
  ): void {
    if (appointmentsMap.has(key)) {
      appointmentsMap.set(key, appointmentsMap.get(key).concat([appointment]));
    } else {
      appointmentsMap.set(key, [appointment]);
    }
  }

  static _sortByDays(appointments: Appointment[], fitters: Fitter[]) {
    return appointments?.slice().sort((a, b) => {
      const fittersMap = groupBy(fitters, ({ id }) => id);

      const timeA = new Date(a.time).getTime();
      const timeB = new Date(b.time).getTime();

      const fitterA = fittersMap[a.fitterId]?.[0] as Fitter;
      const fitterB = fittersMap[b.fitterId]?.[0] as Fitter;

      return timeA - timeB === 0
        ? fitterA?.distance - fitterB?.distance || 0
        : timeA - timeB;
    });
  }

  static _sortByFitterDistance(appointments: Appointment[], fitters: Fitter[]) {
    const fittersMap = groupBy(fitters, ({ id }) => id);

    return appointments?.slice().sort((a, b) => {
      const timeA = new Date(a.time).getTime();
      const timeB = new Date(b.time).getTime();

      const fitterA = fittersMap[a.fitterId]?.[0] as Fitter;
      const fitterB = fittersMap[b.fitterId]?.[0] as Fitter;

      return fitterA?.distance - fitterB?.distance === 0
        ? timeA - timeB
        : fitterA?.distance - fitterB?.distance;
    });
  }

  static _groupFitterById(fitters: Fitter[]): Map<string, Fitter> {
    const result: Map<string, Fitter> = new Map();

    const groupedFitters = groupBy(fitters, (fitter) => {
      return fitter.id;
    });

    forEach(groupedFitters, (value, key) => {
      result.set(key, value[0]);
    });

    return result;
  }

  static _groupByDays(appointments: Appointment[]): Map<string, Appointment[]> {
    const sortedResultList: { day: string; aList: Appointment[] }[] = [];

    appointments.forEach((appointment) => {
      const date = new Date(appointment.time);
      date.setHours(0, 0, 0, 0);
      const dateStr = date.toISOString();

      const foundDateElem = sortedResultList.find(
        (elem) => elem.day === dateStr
      );
      if (foundDateElem) {
        foundDateElem.aList.push(appointment);
      } else {
        sortedResultList.push({ day: dateStr, aList: [appointment] });
      }
    });

    const result = new Map<string, Appointment[]>();

    sortedResultList.sort(
      (elemA, elemB) => Date.parse(elemA.day) - Date.parse(elemB.day)
    );
    sortedResultList.forEach((elem) => {
      result.set(elem.day, elem.aList);
    });

    return result;
  }

  static _groupByFitters(
    appointments: Appointment[]
  ): Map<string, Appointment[]> {
    const result = new Map<string, Appointment[]>();

    appointments.forEach((appointment) => {
      const fitterId = appointment.fitterId;

      if (result.has(fitterId)) {
        result.get(fitterId).push(appointment);
      } else {
        result.set(fitterId, [appointment]);
      }
    });

    return result;
  }

  static _groupByDaysByFitters(
    appointments: Appointment[],
    fitters: Fitter[]
  ): Map<string, Map<string, Appointment[]>> {
    const result: Map<string, Map<string, Appointment[]>> = new Map();
    const appointmentsSorted = this._sortByDays(appointments, fitters);
    const groupedByDays = this._groupByDays(appointmentsSorted);
    const fittersMap = new Map(fitters?.map((fitter) => [fitter.id, fitter]));
    groupedByDays.forEach((dayAppointments: Appointment[], day: string) => {
      const groupedByFitters = this._groupByFitters(dayAppointments);
      const scoredGroupsByFitters = new Map(
        [...groupedByFitters].sort((a, b) => {
          const aa = fittersMap.get(a[0]);
          const bb = fittersMap.get(b[0]);
          return bb?.globalScore - aa?.globalScore || 0;
        })
      );
      result.set(day, scoredGroupsByFitters);
    });

    return result;
  }

  static _groupByFittersByDays(
    appointments: Appointment[],
    fitters: Fitter[]
  ): Map<string, Map<string, Appointment[]>> {
    const result: Map<string, Map<string, Appointment[]>> = new Map();
    const appointmentsSorted = this._sortByFitterDistance(
      appointments,
      fitters
    );

    const groupedByFitters = this._groupByFitters(appointmentsSorted);

    groupedByFitters.forEach(
      (FitterAppointments: Appointment[], fitter: string) => {
        const groupedByDays = this._groupByDays(FitterAppointments);

        result.set(fitter, groupedByDays);
      }
    );

    return result;
  }

  static applyCancelCutOffFeature(appointmentTime, cancelCutoff) {
    const today = new Date();
    const appointmentDate = new Date(appointmentTime);

    if (appointmentDate.getDay() === 1) {
      appointmentDate.setHours(appointmentDate.getHours() - 24);
    }
    appointmentDate.setHours(appointmentDate.getHours() - cancelCutoff);
    return today.getTime() <= appointmentDate.getTime();
  }

  static applyModifCutOffFeature(appointmentTime, modifCutoff) {
    const today = new Date();

    const appointmentDate = new Date(appointmentTime);
    appointmentDate.setHours(appointmentDate.getHours() - modifCutoff);

    if (appointmentDate.getDay() === 1) {
      appointmentDate.setHours(appointmentDate.getHours() - 24);
    }
    return today.getTime() <= appointmentDate.getTime();
  }

  static getFitterLocation(fitter: Fitter): LocationDataGeocoded {
    if (!fitter) {
      return null;
    }
    return {
      address: fitter?.address + ', ' + fitter?.cp + ', ' + fitter?.city,
      geoCoordinates: fitter?.location,
      locationProvider: fitter?.google ? 'GOOGLE' : 'NONE',
      placeId: fitter?.google?.place_id,
      addressElements: {
        city: fitter?.city,
        postalCode: fitter?.cp,
        streetName: fitter?.address,
      },
      isKnownAddress: true,
      isCompleteAddress: true,
    };
  }

  static generateNewAppointmentId(
    fitter: Fitter,
    time: string | Date,
    endTime?: string | Date
  ) {
    return fitter?.brandId && fitter?.id
      ? fitter?.brandId +
          '_' +
          fitter?.id +
          '_' +
          time +
          (endTime ? '_' + endTime : '')
      : null;
  }
}
